/**
 * IssuesPage — EINE produktzentrierte, paginierte Arbeitsliste (IssuesWorkTable).
 * Facetten (Issue-Typ/Schweregrad) + Suche/Sort + Fix via OptimierungenDrawer,
 * gestartet über useOptimierungenLauncher (löst Channel + Felder pro Produkt auf).
 */
import { useWorkspaceIssues } from '@/hooks/useWorkspaceIssues'
import { useRecomputeWorkspace } from '@/hooks/useRecomputeWorkspace'
import { useIssuesFilters } from '@/hooks/useIssuesFilters'
import { useIssuesSegment } from '@/hooks/useIssuesSegment'
import { useChannels } from '@/hooks/useChannels'
import { useRuleCoverage } from '@/hooks/useIssuesInsights'
import { IssuesPageHeader } from '@/components/v2/products/issues/IssuesPageHeader'
import { IssuesProgressBand } from '@/components/v2/products/issues/IssuesProgressBand'
import { NbaCardsRow } from '@/components/v2/products/issues/NbaCardsRow'
import { IssuesAreaTabs } from '@/components/v2/products/issues/IssuesAreaTabs'
import { IssuesSegmentBar } from '@/components/v2/products/issues/IssuesSegmentBar'
import { IssuesFacetsColumn } from '@/components/v2/products/issues/IssuesFacetsColumn'
import { IssuesWorkToolbar } from '@/components/v2/products/issues/IssuesWorkToolbar'
import { IssuesWorkTable } from '@/components/v2/products/issues/IssuesWorkTable'
import { OptimierungenDrawer } from '@/components/v2/products/optimierungen'
import { useOptimierungenLauncher } from '@/components/v2/products/optimierungen/useOptimierungenLauncher'
import { useCreditSystem } from '@/routes/v2/credits/hooks/useCreditSystem'

export function IssuesPage() {
  const { totalIssues, isLoading, products = [] } = useWorkspaceIssues()
  const { data: channels = [] } = useChannels()
  const seg = useIssuesSegment(products, channels)
  const recompute = useRecomputeWorkspace()
  const f = useIssuesFilters(seg.packaged)
  const { data: coverage = [] } = useRuleCoverage()
  const activeChannelName = coverage.find((c) => c.channel_id === f.activeChannel)?.name ?? null
  const { balance } = useCreditSystem()
  const creditBalance = balance ? balance.effective_available.toLocaleString('de-DE') : undefined
  const launcher = useOptimierungenLauncher(creditBalance)

  return (
    <div className="flex h-full flex-col gap-6 p-6">
      <IssuesPageHeader
        productCount={products.length}
        totalIssues={totalIssues}
        recomputePending={recompute.isPending}
        onRecompute={() => recompute.run()}
      />

      <IssuesProgressBand channelId={f.activeChannel} channelName={activeChannelName} />

      <NbaCardsRow />

      <IssuesAreaTabs />

      <IssuesSegmentBar
        rows={products}
        selectedId={seg.selectedId}
        customSegments={seg.customSegments}
        select={seg.select}
        addSegment={seg.addSegment}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 gap-6">
          <IssuesFacetsColumn
            typeCounts={f.typeCounts}
            severityCounts={f.severityCounts}
            activeTypes={f.activeTypes}
            activeSeverities={f.activeSeverities}
            onToggleType={f.toggleType}
            onToggleSeverity={f.toggleSeverity}
            coverage={coverage}
            activeChannel={f.activeChannel}
            onSelectChannel={f.setChannel}
          />
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <IssuesWorkToolbar
              search={f.search}
              onSearchChange={f.setSearch}
              sort={f.sort}
              onSortChange={f.setSort}
              products={products}
            />
            <IssuesWorkTable
              products={f.filtered}
              onFix={(row) => launcher.launch({ product_id: row.id, name: row.name })}
            />
          </div>
        </div>
      )}

      {launcher.props && <OptimierungenDrawer {...launcher.props} />}
    </div>
  )
}
